import React, { useState } from 'react';
import { Badge, Button, Drawer } from 'antd';

import FilterBuilder from '../common/FilterBuilder';
import FilterChips from '../common/FilterChips';
import { FUNNEL } from '../icons';
import ChartRangeControl from '../reports/ChartRangeControl';

/** The first range choice: no range, so each chart keeps its own dates. */
export const OWN_DATES = "Each chart's own dates";

/**
 * A dashboard's controls (charts A6, plan §4.5, §6.5): the date range first,
 * then the filter — the list's own controls (`FilterBuilder`, `FilterChips`)
 * — in the page bar's second row, where a list's filter row sits. They scope
 * every chart on the dashboard: the range replaces each dated chart's own,
 * the filter is ANDed on each chart whose card has every element it names
 * (a chart they cannot reach says so in its own foot).
 *
 * On a phone they fold into a **Filters** button that opens a panel from the
 * bottom (the list's phone filter), with how many are set on the button.
 *
 *   controls  { range, filter } in force (`range` null: each chart's own dates)
 *   onChange  ({ range } | { filter }) => void
 *   hint      a line under them (while arranging: they are saved with it)
 */
export default function DashboardControls({ controls, onChange, fields, collectionId, scopeKey, phone = false, hint = null }) {
	const [open, setOpen] = useState(false);
	const range = controls ? controls.range : null;
	const filter = controls && typeof controls.filter === 'string' ? controls.filter : '';
	const set = (range ? 1 : 0) + (filter.trim() ? 1 : 0);

	const body = (
		<div className={`dashboard-controls${phone ? ' dashboard-controls--panel' : ''}`}>
			<ChartRangeControl
				saved={null}
				value={range}
				savedLabel={OWN_DATES}
				savedValue={null}
				onChange={(next) => onChange({ range: next === undefined ? null : next })}
				phone={phone}
			/>
			<div className='dashboard-controls__filter'>
				<span className='micro-label dashboard-controls__label'>Filter</span>
				<FilterBuilder
					fields={fields}
					query={filter}
					onApply={(text) => onChange({ filter: text || '' })}
					collectionId={collectionId}
					scopeKey={scopeKey}
					emptyText='No conditions yet — each chart counts what it always counts.'
					placeholder='status = open and created > -3mo'
				/>
				{filter.trim() ? (
					<FilterChips query={filter} fields={fields} onChange={(text) => onChange({ filter: text || '' })} className='list-filter__chips dashboard-controls__chips' />
				) : null}
			</div>
			{hint ? <p className='dashboard-controls__hint'>{hint}</p> : null}
		</div>
	);

	if (!phone) return body;
	return (
		<>
			<Badge count={set} size='small' offset={[-4, 4]}>
				<Button className='dashboard-controls__open' icon={<FUNNEL aria-hidden='true' />} onClick={() => setOpen(true)} aria-label={set ? `Filters, ${set} set` : 'Filters'}>
					Filters
				</Button>
			</Badge>
			<Drawer
				open={open}
				placement='bottom'
				height='auto'
				className='dashboard-controls__drawer'
				title='Filters'
				onClose={() => setOpen(false)}
			>
				{body}
			</Drawer>
		</>
	);
}
